import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import {
  SESSION_STORAGE_KEY,
  BILLING_LAYOUT_KEY,
  BILL_TYPE_KEY,
  BILL_ORIENTATION_KEY,
  PANEL_VIEW_KEY,
  IMAGE_URL,
} from "../Constant";
import { DiscountMode, CourierFeePayer, PosMode } from "../Enums";
import type { PageSizeType, PageOrientation } from "../components/templates";
import { message } from "antd";
import { getStockVariantTag } from "../Hook";

export type BillingLayout = "standard" | "compact";
export type PanelViewMode = "tab" | "simple";

export interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  batch_no: string;
  qty: number;
  rawProduct: any;
  cost_price?: number;
  is_tempory_item?: boolean;
  item_name?: string | null;
  description?: string | null;
  is_print?: boolean;
  printed_qty?: number;
  discount_amount?: number;
  discount_input?: number;
  discount_mode?: DiscountMode;
  sold_price?: number;
  variant_id?: string | null;
  variant_name?: string | null;
}

export interface PurchasingCartItem {
  product_id: number | string;
  product_title: string;
  product_sku?: string;
  product_unit?: string;
  product_barcode?: string;
  stock_option: "new" | "existing";
  stock_id?: number | string | null;
  variant_id?: string | null;
  variant_name?: string | null;
  variant_data?: any;
  cost_price: number;
  retail_price: number;
  wholesale_price: number;
  quantity: number;
  profit_margin?: number;
  discount_percent: number;
  total: number;
  batch_no: string;
  expiry_date?: string | null;
  stocks?: any[];
}

export interface QuickAddProduct {
  id: string | number;
  title: string;
  image_paths: string;
  is_negative_qty_allowed?: number;
  stocks?: Array<{
    batch_no: string;
    retail_price: number;
    wholesale_price: number;
    quantity: number;
    is_default?: number;
  }>;
}

interface CartContextType {
  posMode: PosMode;
  setPosMode: (mode: PosMode) => void;
  purchasingCart: PurchasingCartItem[];
  setPurchasingCart: React.Dispatch<React.SetStateAction<PurchasingCartItem[]>>;
  purchasingSupplier: { id: number | string; name: string } | null;
  setPurchasingSupplier: (
    supplier: { id: number | string; name: string } | null,
  ) => void;
  editingPurchaseId: string | number | null;
  setEditingPurchaseId: (id: string | number | null) => void;
  addToPurchasingCart: (item: PurchasingCartItem) => void;
  updatePurchasingCartItem: (
    index: number,
    field: keyof PurchasingCartItem,
    value: any,
  ) => void;
  removeFromPurchasingCart: (index: number) => void;
  clearPurchasingCart: () => void;
  transferPurchasingCartToDrawer: () => void;
  cart: CartItem[];
  cartTotal: number;
  cartItemsCount: number;
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  addToCart: (
    product: any,
    batch: any,
    price: number,
    qty: number,
    rawProduct: any,
    cost_price?: number,
    description?: string | null,
    is_print?: boolean,
    printed_qty?: number,
    discount_amount?: number,
    sold_price?: number,
    discount_input?: number,
    discount_mode?: DiscountMode,
    variant_id?: string | null,
    variant_name?: string | null,
  ) => void;
  quickAdd: (product: QuickAddProduct) => boolean;
  addEditingDraftObj: (obj: any) => void;
  removeFromCart: (id: number, batchNo: string, variantId?: string | null) => void;
  clearCart: () => void;
  customer: { id: number; name: string; credit_limit?: number } | null;
  setCustomer: (
    customer: { id: number; name: string; credit_limit?: number } | null,
  ) => void;
  table: { id: string; name: string } | null;
  setTable: (table: { id: string; name: string } | null) => void;
  isWholeSale: boolean;
  setIsWholeSale: (val: boolean) => void;
  billingLayout: BillingLayout;
  setBillingLayout: (val: BillingLayout) => void;
  billType: PageSizeType;
  setBillType: (val: PageSizeType) => void;
  orientation: PageOrientation;
  setOrientation: (val: PageOrientation) => void;
  panelViewMode: PanelViewMode;
  setPanelViewMode: (val: PanelViewMode) => void;
  editingOrderId: string | null;
  setEditingOrderId: (id: string | null) => void;
  editingDraftId: string | null;
  setEditingDraftId: (id: string | null) => void;
  editingQuotationId: string | null;
  setEditingQuotationId: (id: string | null) => void;
  editingQuotationName: string | null;
  setEditingQuotationName: (name: string | null) => void;
  proceedingQuotationId: string | null;
  setProceedingQuotationId: (id: string | null) => void;
  quotationTempCustomer: {
    name: string;
    phone?: string;
    email?: string;
    address?: string;
  } | null;
  setQuotationTempCustomer: (
    cust: {
      name: string;
      phone?: string;
      email?: string;
      address?: string;
    } | null,
  ) => void;
  exchangeDiscount: number;
  setExchangeDiscount: (val: number) => void;
  originalDraftItems: any[];
  setOriginalDraftItems: (items: any[]) => void;
  repairJobId: string | null;
  setRepairJobId: (id: string | null) => void;
  repairJobAdvance: number;
  setRepairJobAdvance: (val: number) => void;
  repairJobPaymentMethodId: string | null;
  setRepairJobPaymentMethodId: (id: string | null) => void;
  onlineOrderId: string | null;
  setOnlineOrderId: (id: string | null) => void;
  courierId: string | null;
  setCourierId: (id: string | null | undefined) => void;
  courierTrackingNo: string | null;
  setCourierTrackingNo: (no: string | null | undefined) => void;
  courierIsCOD: boolean;
  setCourierIsCOD: (val: boolean) => void;
  courierDeliveryFee: number;
  setCourierDeliveryFee: (val: number) => void;
  courierFeePayer: CourierFeePayer;
  setCourierFeePayer: (val: CourierFeePayer) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  // ─── POS Purchasing Mode ──────────────────────────────────────────────────
  const [posMode, setPosModeState] = useState<PosMode>(() => {
    try {
      const stored = sessionStorage.getItem("pos_mode");
      return (stored as PosMode) || PosMode.SALE;
    } catch {
      return PosMode.SALE;
    }
  });

  const setPosMode = useCallback((mode: PosMode) => {
    setPosModeState(mode);
    sessionStorage.setItem("pos_mode", mode);
  }, []);

  const [purchasingCart, setPurchasingCart] = useState<PurchasingCartItem[]>(() => {
    try {
      const stored = sessionStorage.getItem("pos_purchasing_cart_data");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [purchasingSupplier, setPurchasingSupplierState] = useState<{
    id: number | string;
    name: string;
  } | null>(() => {
    try {
      const stored = sessionStorage.getItem("pos_purchasing_supplier");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setPurchasingSupplier = useCallback(
    (supp: { id: number | string; name: string } | null) => {
      setPurchasingSupplierState(supp);
      if (supp) {
        sessionStorage.setItem("pos_purchasing_supplier", JSON.stringify(supp));
      } else {
        sessionStorage.removeItem("pos_purchasing_supplier");
      }
    },
    [],
  );

  useEffect(() => {
    try {
      sessionStorage.setItem(
        "pos_purchasing_cart_data",
        JSON.stringify(purchasingCart),
      );
    } catch (e) {
      console.error("Error saving purchasingCart to sessionStorage", e);
    }
  }, [purchasingCart]);

  const addToPurchasingCart = useCallback((newItem: PurchasingCartItem) => {
    setPurchasingCart((prev) => {
      const existingIndex = prev.findIndex(
        (item) =>
          String(item.product_id) === String(newItem.product_id) &&
          item.batch_no === newItem.batch_no &&
          (item.variant_id ?? null) === (newItem.variant_id ?? null),
      );
      if (existingIndex > -1) {
        const updated = [...prev];
        const existing = updated[existingIndex];
        const newQty = existing.quantity + newItem.quantity;
        const cost = newItem.cost_price || existing.cost_price;
        const retail = newItem.retail_price || existing.retail_price;
        const discountPct = newItem.discount_percent ?? existing.discount_percent ?? 0;
        const subtotal = cost * newQty;
        const total = subtotal - (subtotal * discountPct) / 100;
        const margin = cost > 0 ? ((retail - cost) / cost) * 100 : 0;
        updated[existingIndex] = {
          ...existing,
          ...newItem,
          quantity: newQty,
          cost_price: cost,
          retail_price: retail,
          discount_percent: discountPct,
          total,
          profit_margin: margin,
        };
        return updated;
      }
      return [...prev, newItem];
    });
  }, []);

  const updatePurchasingCartItem = useCallback(
    (index: number, field: keyof PurchasingCartItem, value: any) => {
      setPurchasingCart((prev) => {
        if (index < 0 || index >= prev.length) return prev;
        const updated = [...prev];
        const item = { ...updated[index], [field]: value };

        const cost = item.cost_price || 0;
        const retail = item.retail_price || 0;
        const qty = item.quantity || 1;

        if (field === "cost_price" || field === "retail_price") {
          item.profit_margin = cost > 0 ? ((retail - cost) / cost) * 100 : 0;
        }

        if (field === "quantity" || field === "cost_price" || field === "discount_percent") {
          const discountPct = item.discount_percent || 0;
          const subtotal = cost * qty;
          item.total = subtotal - (subtotal * discountPct) / 100;
        }

        updated[index] = item;
        return updated;
      });
    },
    [],
  );

  const removeFromPurchasingCart = useCallback((index: number) => {
    setPurchasingCart((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const [editingPurchaseId, setEditingPurchaseIdState] = useState<string | number | null>(() => {
    try {
      return sessionStorage.getItem("pos_editing_purchase_id") || null;
    } catch {
      return null;
    }
  });

  const setEditingPurchaseId = useCallback((id: string | number | null) => {
    setEditingPurchaseIdState(id);
    if (id) {
      sessionStorage.setItem("pos_editing_purchase_id", String(id));
    } else {
      sessionStorage.removeItem("pos_editing_purchase_id");
    }
  }, []);

  const clearPurchasingCart = useCallback(() => {
    setPurchasingCart([]);
    setPurchasingSupplier(null);
    setEditingPurchaseId(null);
    sessionStorage.removeItem("pos_purchasing_cart_data");
    sessionStorage.removeItem("pos_purchasing_supplier");
    sessionStorage.removeItem("pos_editing_purchase_id");
  }, [setPurchasingSupplier, setEditingPurchaseId]);

  const transferPurchasingCartToDrawer = useCallback(() => {
    try {
      sessionStorage.setItem(
        "pos_purchasing_cart_draft",
        JSON.stringify({
          supplier_id: purchasingSupplier?.id || null,
          items: purchasingCart,
        }),
      );
    } catch (e) {
      console.error("Error setting pos_purchasing_cart_draft", e);
    }
  }, [purchasingSupplier, purchasingCart]);

  // ─── Cart ──────────────────────────────────────────────────────────────────
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // ─── Customer ──────────────────────────────────────────────────────────────
  const [customer, setCustomerState] = useState<{
    id: number;
    name: string;
    credit_limit?: number;
  } | null>(() => {
    try {
      const stored = sessionStorage.getItem("pos_customer");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // ─── Table ─────────────────────────────────────────────────────────────────
  const [table, setTableState] = useState<{ id: string; name: string } | null>(
    () => {
      try {
        const stored = sessionStorage.getItem("pos_table");
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    },
  );

  // ─── Wholesale ─────────────────────────────────────────────────────────────
  const [isWholeSale, setIsWholeSaleState] = useState<boolean>(() => {
    try {
      const stored = sessionStorage.getItem("pos_is_wholesale");
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  // ─── Editing Order ──────────────────────────────────────────────────────────
  const [editingOrderId, setEditingOrderIdState] = useState<string | null>(
    () => {
      try {
        return sessionStorage.getItem("pos_editing_order_id");
      } catch {
        return null;
      }
    },
  );

  // ─── Editing Draft ──────────────────────────────────────────────────────────
  const [editingDraftId, setEditingDraftIdState] = useState<string | null>(
    () => {
      try {
        return sessionStorage.getItem("pos_editing_draft_id");
      } catch {
        return null;
      }
    },
  );

  const [editingQuotationId, setEditingQuotationIdState] = useState<
    string | null
  >(() => {
    try {
      return sessionStorage.getItem("pos_editing_quotation_id");
    } catch {
      return null;
    }
  });

  const [editingQuotationName, setEditingQuotationNameState] = useState<
    string | null
  >(() => {
    try {
      return sessionStorage.getItem("pos_editing_quotation_name");
    } catch {
      return null;
    }
  });

  const [proceedingQuotationId, setProceedingQuotationIdState] = useState<
    string | null
  >(() => {
    try {
      return sessionStorage.getItem("pos_proceeding_quotation_id");
    } catch {
      return null;
    }
  });

  const [quotationTempCustomer, setQuotationTempCustomerState] = useState<any>(
    () => {
      try {
        const stored = sessionStorage.getItem("pos_quotation_temp_customer");
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    },
  );

  // ─── Exchange Discount (Returned Item Exchange Offset) ──────────────────────
  const [exchangeDiscount, setExchangeDiscountState] = useState<number>(() => {
    try {
      const stored = sessionStorage.getItem("pos_exchange_discount");
      return stored ? parseFloat(stored) : 0;
    } catch {
      return 0;
    }
  });

  // ─── POS Preferences (localStorage) ───────────────────────────────────────
  const [billingLayout, setBillingLayoutState] = useState<BillingLayout>(
    () =>
      (localStorage.getItem(BILLING_LAYOUT_KEY) as BillingLayout) || "standard",
  );

  const [billType, setBillTypeState] = useState<PageSizeType>(
    () => (localStorage.getItem(BILL_TYPE_KEY) as PageSizeType) || "80mm",
  );

  const [orientation, setOrientationState] = useState<PageOrientation>(
    () =>
      (localStorage.getItem(BILL_ORIENTATION_KEY) as PageOrientation) ||
      "portrait",
  );

  const [panelViewMode, setPanelViewModeState] = useState<PanelViewMode>(
    () => (localStorage.getItem(PANEL_VIEW_KEY) as PanelViewMode) || "tab",
  );

  // ─── Repair Job POS Context State ──────────────────────────────────────────
  const [repairJobId, setRepairJobIdState] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("pos_repair_job_id");
    } catch {
      return null;
    }
  });

  const [repairJobAdvance, setRepairJobAdvanceState] = useState<number>(() => {
    try {
      const stored = sessionStorage.getItem("pos_repair_job_advance");
      return stored ? parseFloat(stored) : 0;
    } catch {
      return 0;
    }
  });

  const [repairJobPaymentMethodId, setRepairJobPaymentMethodIdState] = useState<
    string | null
  >(() => {
    try {
      return sessionStorage.getItem("pos_repair_job_pm_id");
    } catch {
      return null;
    }
  });

  // ─── Online Order POS Context State ────────────────────────────────────────
  const [onlineOrderId, setOnlineOrderIdState] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("pos_online_order_id");
    } catch {
      return null;
    }
  });

  // ─── Courier Details State ─────────────────────────────────────────────────
  const [courierId, setCourierIdState] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("pos_courier_id");
    } catch {
      return null;
    }
  });

  const [courierTrackingNo, setCourierTrackingNoState] = useState<
    string | null
  >(() => {
    try {
      return sessionStorage.getItem("pos_courier_tracking_no");
    } catch {
      return null;
    }
  });

  const [courierIsCOD, setCourierIsCODState] = useState<boolean>(() => {
    try {
      const stored = sessionStorage.getItem("pos_courier_is_cod");
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  const [courierDeliveryFee, setCourierDeliveryFeeState] = useState<number>(
    () => {
      try {
        const stored = sessionStorage.getItem("pos_courier_delivery_fee");
        return stored ? parseFloat(stored) : 0;
      } catch {
        return 0;
      }
    },
  );

  const [courierFeePayer, setCourierFeePayerState] = useState<CourierFeePayer>(
    () => {
      try {
        const stored = sessionStorage.getItem(
          "pos_courier_fee_payer",
        ) as CourierFeePayer;
        return stored || CourierFeePayer.CUSTOMER;
      } catch {
        return CourierFeePayer.CUSTOMER;
      }
    },
  );

  // ─── Setters ───────────────────────────────────────────────────────────────
  const setCourierId = useCallback((id: string | null | undefined) => {
    const val = id || null;
    setCourierIdState(val);
    if (val !== null) {
      sessionStorage.setItem("pos_courier_id", val);
    } else {
      sessionStorage.removeItem("pos_courier_id");
    }
  }, []);

  const setCourierTrackingNo = useCallback((no: string | null | undefined) => {
    const val = no || null;
    setCourierTrackingNoState(val);
    if (val !== null) {
      sessionStorage.setItem("pos_courier_tracking_no", val);
    } else {
      sessionStorage.removeItem("pos_courier_tracking_no");
    }
  }, []);

  const setCourierIsCOD = useCallback((isCOD: boolean) => {
    setCourierIsCODState(isCOD);
    sessionStorage.setItem("pos_courier_is_cod", JSON.stringify(isCOD));
  }, []);

  const setCourierDeliveryFee = useCallback((fee: number) => {
    setCourierDeliveryFeeState(fee);
    sessionStorage.setItem("pos_courier_delivery_fee", String(fee));
  }, []);

  const setCourierFeePayer = useCallback((payer: CourierFeePayer) => {
    setCourierFeePayerState(payer);
    sessionStorage.setItem("pos_courier_fee_payer", payer);
  }, []);
  const setCustomer = useCallback(
    (cust: { id: number; name: string; credit_limit?: number } | null) => {
      setCustomerState(cust);
      if (cust) {
        sessionStorage.setItem("pos_customer", JSON.stringify(cust));
      } else {
        sessionStorage.removeItem("pos_customer");
      }
    },
    [],
  );

  const setTable = useCallback((tbl: { id: string; name: string } | null) => {
    setTableState(tbl);
    if (tbl) {
      sessionStorage.setItem("pos_table", JSON.stringify(tbl));
    } else {
      sessionStorage.removeItem("pos_table");
    }
  }, []);

  const setIsWholeSale = useCallback((val: boolean) => {
    setIsWholeSaleState(val);
    sessionStorage.setItem("pos_is_wholesale", JSON.stringify(val));
  }, []);

  const setBillingLayout = useCallback((val: BillingLayout) => {
    setBillingLayoutState(val);
    localStorage.setItem(BILLING_LAYOUT_KEY, val);
  }, []);

  const setBillType = useCallback((val: PageSizeType) => {
    setBillTypeState(val);
    localStorage.setItem(BILL_TYPE_KEY, val);
  }, []);

  const setOrientation = useCallback((val: PageOrientation) => {
    setOrientationState(val);
    localStorage.setItem(BILL_ORIENTATION_KEY, val);
  }, []);

  const setPanelViewMode = useCallback((val: PanelViewMode) => {
    setPanelViewModeState(val);
    localStorage.setItem(PANEL_VIEW_KEY, val);
  }, []);

  const setEditingOrderId = useCallback((id: string | null) => {
    setEditingOrderIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_editing_order_id", id);
    } else {
      sessionStorage.removeItem("pos_editing_order_id");
    }
  }, []);

  const setEditingDraftId = useCallback((id: string | null) => {
    setEditingDraftIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_editing_draft_id", id);
    } else {
      sessionStorage.removeItem("pos_editing_draft_id");
    }
  }, []);

  const setEditingQuotationId = useCallback((id: string | null) => {
    setEditingQuotationIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_editing_quotation_id", id);
    } else {
      sessionStorage.removeItem("pos_editing_quotation_id");
    }
  }, []);

  const setEditingQuotationName = useCallback((name: string | null) => {
    setEditingQuotationNameState(name);
    if (name !== null) {
      sessionStorage.setItem("pos_editing_quotation_name", name);
    } else {
      sessionStorage.removeItem("pos_editing_quotation_name");
    }
  }, []);

  const setProceedingQuotationId = useCallback((id: string | null) => {
    setProceedingQuotationIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_proceeding_quotation_id", id);
    } else {
      sessionStorage.removeItem("pos_proceeding_quotation_id");
    }
  }, []);

  const setQuotationTempCustomer = useCallback((cust: any) => {
    setQuotationTempCustomerState(cust);
    if (cust !== null) {
      sessionStorage.setItem(
        "pos_quotation_temp_customer",
        JSON.stringify(cust),
      );
    } else {
      sessionStorage.removeItem("pos_quotation_temp_customer");
    }
  }, []);

  const setExchangeDiscount = useCallback((val: number) => {
    setExchangeDiscountState(val);
    sessionStorage.setItem("pos_exchange_discount", String(val));
  }, []);

  const setRepairJobId = useCallback((id: string | null) => {
    setRepairJobIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_repair_job_id", id);
    } else {
      sessionStorage.removeItem("pos_repair_job_id");
    }
  }, []);

  const setRepairJobAdvance = useCallback((val: number) => {
    setRepairJobAdvanceState(val);
    sessionStorage.setItem("pos_repair_job_advance", String(val));
  }, []);

  const setRepairJobPaymentMethodId = useCallback((id: string | null) => {
    setRepairJobPaymentMethodIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_repair_job_pm_id", id);
    } else {
      sessionStorage.removeItem("pos_repair_job_pm_id");
    }
  }, []);

  const setOnlineOrderId = useCallback((id: string | null) => {
    setOnlineOrderIdState(id);
    if (id !== null) {
      sessionStorage.setItem("pos_online_order_id", id);
    } else {
      sessionStorage.removeItem("pos_online_order_id");
    }
  }, []);

  const [originalDraftItems, setOriginalDraftItems] = useState<any[]>(() => {
    try {
      const stored = sessionStorage.getItem("pos_original_draft_items");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify(
          cart.map((item) => ({
            id: item.id,
            name: item.name,
            price: item.price,
            image: item.image,
            batch_no: item.batch_no,
            qty: item.qty,
            rawProduct: item.rawProduct,
            cost_price: item.cost_price,
            is_tempory_item: item.is_tempory_item,
            item_name: item.item_name,
            description: item.description,
            is_print: item.is_print,
            printed_qty: item.printed_qty,
            discount_amount: item.discount_amount,
            discount_input: item.discount_input,
            discount_mode: item.discount_mode,
            sold_price: item.sold_price,
            variant_id: item.variant_id,
            variant_name: item.variant_name,
          })),
        ),
      );
    } catch (e) {
      console.error("Error saving cart to sessionStorage", e);
    }
  }, [cart]);

  // ─── Cart actions ──────────────────────────────────────────────────────────
  const addToCart = useCallback(
    (
      product: any,
      batch: any,
      price: number,
      qty: number,
      rawProduct: any,
      cost_price?: number,
      description?: string | null,
      is_print?: boolean,
      printed_qty?: number,
      discount_amount?: number,
      sold_price?: number,
      discount_input?: number,
      discount_mode?: DiscountMode,
      variant_id?: string | null,
      variant_name?: string | null,
    ) => {
      setCart((prevCart) => {
        const vId = variant_id ?? rawProduct?.variant_id ?? batch?.variant_id ?? null;
        const vName =
          variant_name ??
          rawProduct?.variant_name ??
          (batch ? getStockVariantTag(batch) : null) ??
          null;

        const existing = prevCart.find(
          (item) =>
            item.id === product.id &&
            item.batch_no === batch.batch_no &&
            (item.variant_id ?? null) === vId,
        );
        let updated: CartItem[];

        if (existing) {
          updated = prevCart.map((item) =>
            item.id === product.id &&
            item.batch_no === batch.batch_no &&
            (item.variant_id ?? null) === vId
              ? {
                  ...item,
                  qty: item.qty + qty,
                  price,
                  cost_price: cost_price ?? item.cost_price,
                  description:
                    description !== undefined ? description : item.description,
                  is_print: is_print !== undefined ? is_print : item.is_print,
                  printed_qty:
                    printed_qty !== undefined ? printed_qty : item.printed_qty,
                  discount_amount:
                    discount_amount !== undefined
                      ? discount_amount
                      : item.discount_amount,
                  sold_price:
                    sold_price !== undefined ? sold_price : item.sold_price,
                  discount_input:
                    discount_input !== undefined
                      ? discount_input
                      : item.discount_input,
                  discount_mode:
                    discount_mode !== undefined
                      ? discount_mode
                      : item.discount_mode,
                  variant_id: vId,
                  variant_name: vName,
                }
              : item,
          );
        } else {
          updated = [
            ...prevCart,
            {
              id: product.id,
              name: product.title,
              price: price,
              image: product.image,
              batch_no: batch.batch_no,
              qty: qty,
              rawProduct: rawProduct,
              cost_price: cost_price,
              is_tempory_item: rawProduct?.is_tempory_item || false,
              item_name: null,
              description: description || null,
              is_print: is_print || false,
              printed_qty: printed_qty || 0,
              discount_amount: discount_amount ?? 0,
              discount_input: discount_input ?? 0,
              discount_mode: discount_mode ?? DiscountMode.AMOUNT,
              sold_price: sold_price ?? 0,
              variant_id: vId,
              variant_name: vName,
            },
          ];
        }
        return updated;
      });
    },
    [],
  );

  const quickAdd = useCallback(
    (product: QuickAddProduct) => {
      const defaultStock =
        product.stocks?.find((s) => s.is_default === 1) || product.stocks?.[0];

      if (!defaultStock) {
        message.warning("This product has no stock batch to add.");
        return false;
      }

      const isNegativeAllowed = product.is_negative_qty_allowed === 1;

      const cartQtyForBatch = cart
        .filter(
          (item) =>
            String(item.id) === String(product.id) &&
            item.batch_no === defaultStock.batch_no,
        )
        .reduce((sum, item) => sum + item.qty, 0);
      const availableQty = (defaultStock.quantity ?? 0) - cartQtyForBatch;

      if (!isNegativeAllowed && availableQty <= 0) {
        message.warning(
          `No stock available for ${product.title}. Only ${Math.max(
            availableQty,
            0,
          )} available.`,
        );
        return false;
      }

      const price = isWholeSale
        ? defaultStock.wholesale_price
        : defaultStock.retail_price;

      let mainImage = "";
      try {
        const imgs = JSON.parse(product.image_paths || "[]");
        if (imgs.length > 0) {
          mainImage = `${IMAGE_URL}/${imgs[0]}`;
        }
      } catch {
        // ignore invalid image paths
      }

      addToCart(
        { id: product.id, title: product.title, image: mainImage },
        defaultStock,
        price,
        1,
        product,
        undefined,
      );
      message.success(`${product.title} added to cart.`);
      return true;
    },
    [isWholeSale, addToCart, cart],
  );

  const addEditingDraftObj = useCallback((obj: any) => {
    try {
      if (obj === null) {
        sessionStorage.removeItem("pos_editing_draft_obj");
        return;
      }
      sessionStorage.setItem("pos_editing_draft_obj", JSON.stringify(obj));
    } catch (e) {
      console.error("Error saving cart to sessionStorage", e);
    }
  }, []);

  const removeFromCart = useCallback((id: number, batchNo: string, variantId?: string | null) => {
    setCart((prevCart) =>
      prevCart.filter(
        (item) =>
          !(
            item.id === id &&
            item.batch_no === batchNo &&
            (variantId ? item.variant_id === variantId : true)
          ),
      ),
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    setCustomer(null);
    setTable(null);
    setEditingOrderId(null);
    setEditingDraftId(null);
    setEditingQuotationId(null);
    setEditingQuotationName(null);
    setProceedingQuotationId(null);
    setQuotationTempCustomer(null);
    setExchangeDiscount(0);
    setRepairJobId(null);
    setRepairJobAdvance(0);
    setRepairJobPaymentMethodId(null);
    setOnlineOrderId(null);
    setCourierId(null);
    setCourierTrackingNo(null);
    setCourierIsCOD(false);
    setCourierDeliveryFee(0);
    setCourierFeePayer(CourierFeePayer.CUSTOMER);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem("pos_editing_draft_id");
    sessionStorage.removeItem("pos_original_draft_items");
    sessionStorage.removeItem("pos_editing_quotation_id");
    sessionStorage.removeItem("pos_editing_quotation_name");
    sessionStorage.removeItem("pos_proceeding_quotation_id");
    sessionStorage.removeItem("pos_quotation_temp_customer");
    sessionStorage.removeItem("pos_repair_job_id");
    sessionStorage.removeItem("pos_repair_job_advance");
    sessionStorage.removeItem("pos_repair_job_pm_id");
    sessionStorage.removeItem("pos_online_order_id");
    sessionStorage.removeItem("pos_courier_id");
    sessionStorage.removeItem("pos_courier_tracking_no");
    sessionStorage.removeItem("pos_courier_is_cod");
    sessionStorage.removeItem("pos_courier_delivery_fee");
    sessionStorage.removeItem("pos_courier_fee_payer");
  }, [
    setCustomer,
    setTable,
    setEditingOrderId,
    setEditingDraftId,
    setEditingQuotationId,
    setEditingQuotationName,
    setProceedingQuotationId,
    setQuotationTempCustomer,
    setExchangeDiscount,
    setRepairJobId,
    setRepairJobAdvance,
    setRepairJobPaymentMethodId,
    setOnlineOrderId,
    setCourierId,
    setCourierTrackingNo,
    setCourierIsCOD,
    setCourierDeliveryFee,
    setCourierFeePayer,
  ]);

  const cartTotal = useMemo(() => {
    return cart.reduce(
      (acc, item) => acc + item.price * item.qty - (item.discount_amount ?? 0),
      0,
    );
  }, [cart]);

  const cartItemsCount = useMemo(() => {
    return cart.length;
  }, [cart]);

  const contextValue = useMemo(
    () => ({
      posMode,
      setPosMode,
      purchasingCart,
      setPurchasingCart,
      purchasingSupplier,
      setPurchasingSupplier,
      editingPurchaseId,
      setEditingPurchaseId,
      addToPurchasingCart,
      updatePurchasingCartItem,
      removeFromPurchasingCart,
      clearPurchasingCart,
      transferPurchasingCartToDrawer,
      cart,
      cartTotal,
      cartItemsCount,
      customer,
      setCustomer,
      table,
      setTable,
      addToCart,
      quickAdd,
      addEditingDraftObj,
      removeFromCart,
      clearCart,
      setCart,
      isWholeSale,
      setIsWholeSale,
      billingLayout,
      setBillingLayout,
      billType,
      setBillType,
      orientation,
      setOrientation,
      panelViewMode,
      setPanelViewMode,
      editingOrderId,
      setEditingOrderId,
      editingDraftId,
      setEditingDraftId,
      editingQuotationId,
      setEditingQuotationId,
      editingQuotationName,
      setEditingQuotationName,
      proceedingQuotationId,
      setProceedingQuotationId,
      quotationTempCustomer,
      setQuotationTempCustomer,
      exchangeDiscount,
      setExchangeDiscount,
      originalDraftItems,
      setOriginalDraftItems,
      repairJobId,
      setRepairJobId,
      repairJobAdvance,
      setRepairJobAdvance,
      repairJobPaymentMethodId,
      setRepairJobPaymentMethodId,
      onlineOrderId,
      setOnlineOrderId,
      courierId,
      setCourierId,
      courierTrackingNo,
      setCourierTrackingNo,
      courierIsCOD,
      setCourierIsCOD,
      courierDeliveryFee,
      setCourierDeliveryFee,
      courierFeePayer,
      setCourierFeePayer,
    }),
    [
      posMode,
      setPosMode,
      purchasingCart,
      setPurchasingCart,
      purchasingSupplier,
      setPurchasingSupplier,
      editingPurchaseId,
      setEditingPurchaseId,
      addToPurchasingCart,
      updatePurchasingCartItem,
      removeFromPurchasingCart,
      clearPurchasingCart,
      transferPurchasingCartToDrawer,
      cart,
      cartTotal,
      cartItemsCount,
      customer,
      setCustomer,
      table,
      setTable,
      addToCart,
      quickAdd,
      addEditingDraftObj,
      removeFromCart,
      clearCart,
      isWholeSale,
      setIsWholeSale,
      billingLayout,
      setBillingLayout,
      billType,
      setBillType,
      orientation,
      setOrientation,
      panelViewMode,
      setPanelViewMode,
      editingOrderId,
      setEditingOrderId,
      editingDraftId,
      setEditingDraftId,
      editingQuotationId,
      setEditingQuotationId,
      editingQuotationName,
      setEditingQuotationName,
      proceedingQuotationId,
      setProceedingQuotationId,
      quotationTempCustomer,
      setQuotationTempCustomer,
      exchangeDiscount,
      setExchangeDiscount,
      originalDraftItems,
      setOriginalDraftItems,
      repairJobId,
      setRepairJobId,
      repairJobAdvance,
      setRepairJobAdvance,
      repairJobPaymentMethodId,
      setRepairJobPaymentMethodId,
      onlineOrderId,
      setOnlineOrderId,
      courierId,
      setCourierId,
      courierTrackingNo,
      setCourierTrackingNo,
      courierIsCOD,
      setCourierIsCOD,
      courierDeliveryFee,
      setCourierDeliveryFee,
      courierFeePayer,
      setCourierFeePayer,
    ],
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
